import { infoUser, getAllPermissionsAttachedToUser, listUser, ssoUser, deleteUserSImage } from '@goldcast/api/backend';
import { Auth } from 'aws-amplify';
import debounce from 'lodash.debounce';
import { GenericMessageDialog, IframeEmbedder } from 'uicomponents';

import { UserGetters } from './getters';
import { UserMutations } from './mutations';
import { UserState, LiteModeKeys } from './types';
import { formatAgenda, mapTracksToTickets, setIsLiveAndTrigger } from '../event/actions';

import type { Broadcast, AgendaItem } from '../event/types';

import analytics from '@/analytics';
import { getCloudFrontUrl, getEnv, UPLOAD_PREFIX } from '@/constants';
import { PREVIOUS_AUTH_SYSTEM_KEY } from '@/constants/authKeys';
import { ChatEvents } from '@/libs/eventBus/chatEvents';
import EventBus from '@/libs/eventBus/eventBus';
import DialogService from '@/services/dialogs/DialogService';
import NotificationService from '@/services/dialogs/notifications';
import { addAgendaFavorite, getEventUsersById, removeAgendaFavorite } from '@/services/event.service';
import { GCAuth, GCAUTH_ACCESS_TOKEN_KEY, GCAUTH_REFRESH_TOKEN_KEY } from '@/services/gcauth.service';
import { closeJoiningDelayNetworkErrorModal } from '@/services/liveMedia.service';
import {
  deleteCookie,
  getCookieValue,
  getValueFromLocalstore,
  saveValueToLocalstore,
  setCookieValue
} from '@/services/localStorage.service';
import loggerService from '@/services/logger.service';
import { updateUserProfile } from '@/services/organization.service';
import { uploadProfileJpeg } from '@/services/user_image.service';
import uuidGen from '@/services/utils/uuid.js';
import { getVideoQNARequestForCurrentUserByStatus } from '@/services/videoQNA.service';
import { RootState } from '@/store';
import { storeGCAuthTokens } from '@/utils/magicLinkAuth';
import { pushRoute } from '@/utils/routerAccess';
import { safeSignout } from '@/utils/safeSignout';
import { makeUser } from '@/utils/userTransform';

export interface UserContext {
  state: UserState;
  rootState: RootState;
  getters: UserGetters;
  rootGetters: { [k: string]: any };
  commit: <k extends keyof UserMutations>(mutation: k, payload?: any, config?: { root: boolean }) => any;
  dispatch: (action: string, params?: any, settings?: { root: boolean }) => Promise<any>;
}

async function handleAuthError(context: UserContext, message: string, error?: any) {
  // The router guard handles redirecting unauthenticated users to /auth (or Cvent OAuth).
  // The fetch interceptor handles mid-session 401s.
  // This function clears stale tokens, signs out, and logs the error.
  loggerService.warn(message, error);

  context.commit('setAccessToken', null);
  context.commit('setRefreshToken', null);

  return context.dispatch('signUserOut');
}

function getStoredTokens(context: UserContext): { accessToken: string | null; refreshToken: string | null } {
  let accessToken = context.state.accessToken;
  let refreshToken = context.state.refreshToken;

  if (!accessToken) {
    const storedToken = getValueFromLocalstore(GCAUTH_ACCESS_TOKEN_KEY);
    if (storedToken) {
      accessToken = storedToken;
      context.commit('setAccessToken', accessToken);
    }
  }

  if (!refreshToken) {
    const storedToken = getValueFromLocalstore(GCAUTH_REFRESH_TOKEN_KEY);
    if (storedToken) {
      refreshToken = storedToken;
      context.commit('setRefreshToken', refreshToken);
    }
  }

  return { accessToken: accessToken ?? null, refreshToken: refreshToken ?? null };
}

async function fetchGCAuthAccessToken(context: UserContext, isUnauthorized: boolean, error: string) {
  const { accessToken, refreshToken } = getStoredTokens(context);

  if (!accessToken || !refreshToken) {
    return handleAuthError(context, 'Neither access token nor refresh token available for GCAuth');
  }

  return GCAuth.getCurrentAccessToken(accessToken, refreshToken, isUnauthorized, error)
    .then(({ accessToken, refreshToken }) => {
      storeGCAuthTokens(context, accessToken, refreshToken);
      return accessToken;
    })
    .catch(err => {
      return handleAuthError(context, 'Error in fetching access token: ', err);
    });
}

async function fetchAmplifyAccessToken(context: UserContext) {
  return Auth.currentAuthenticatedUser()
    .then(user => {
      context.state.accessToken = user.signInUserSession.accessToken.jwtToken;
      return context.state.accessToken;
    })
    .catch(err => {
      return handleAuthError(context, 'Error in fetching access token: ', err);
    });
}

async function handleGCAuthSSOResponse(context: UserContext, res: any) {
  loggerService.log('SSOLogin: getTokenUsingSSO');

  if (res.error) {
    loggerService.error('SSOLogin: GCAuth SSO failed', res);
    throw new Error(res.message || 'Unable to authenticate with identity provider');
  }

  if (res.access_token && res.refresh_token && res.email) {
    const email = res.email;

    if (context.state.currentUser?.email === email) {
      loggerService.log('SSOLogin: Already logged in');
      return { isGCAuth: true };
    }

    await safeSignout();
    loggerService.log('SSOLogin: Signout completed');
    context.dispatch('RESET_USER');

    loggerService.log('SSOLogin: GCAuth SSO successful');
    storeGCAuthTokens(context, res.access_token, res.refresh_token);
    return { isGCAuth: true };
  }

  throw new Error('Invalid GCAuth response format');
}

async function handleAmplifySSOResponse(context: UserContext, res: any) {
  loggerService.log('SSOLogin: getTokenUsingSSO');

  const email = res.email;
  const secretCode = res.token;

  if (context.state.currentUser?.email === email) {
    loggerService.log('SSOLogin: Already logged in');
    return { email, secretCode: '' };
  }

  await safeSignout();
  loggerService.log('SSOLogin: Signout completed');
  context.dispatch('RESET_USER');
  return { email, secretCode };
}

async function performAmplifyLogin(context: UserContext, email: string, secretCode: string) {
  loggerService.log('SSOLogin: Secretcode ', secretCode);

  if (!secretCode) {
    loggerService.log('SSOLogin: Refetching logged in user');
    return context.dispatch('fetchCurrentUser');
  }

  const user = await Auth.signIn(email);
  loggerService.log('SSOLogin: Custom challenge ', secretCode);

  const cognitoUser = await Auth.sendCustomChallengeAnswer(user, secretCode, { app: 'event' });

  if (!cognitoUser.signInUserSession) {
    loggerService.log('SSOLogin: Invalid token');
    throw new Error('Invalid token');
  }

  saveValueToLocalstore(PREVIOUS_AUTH_SYSTEM_KEY, 'cognito');
  loggerService.log('SSOLogin: Fetching user after login');
  return context.dispatch('fetchCurrentUser');
}

async function completeLoginFlow(context: UserContext, result: any, eventId: string) {
  if (result.isGCAuth) {
    loggerService.log('SSOLogin: Fetching user after GCAuth SSO login');
    await context.dispatch('fetchCurrentUser');
  } else {
    const { email, secretCode } = result;
    await performAmplifyLogin(context, email, secretCode);
  }

  loggerService.log('SSOLogin: Load eventuser');
  return context.dispatch('loadEventUser', {
    eventID: eventId,
    force: true
  });
}

function handleSSOError(context: UserContext, err: any) {
  loggerService.warn(err);
  (context.commit as any)('event/setSSOProcessing', null, { root: true });

  if (err?.code === 'TooManyRequestsException') {
    alert('Login failed due to server load! Open link again in some time');
  } else {
    pushRoute({
      name: 'ErrorPage403',
      query: {
        message: 'EVENT_ACCESS_NOT_ALLOWED'
      }
    });
  }
}

async function getGCAuthTokenUsingSSO(code: string) {
  return GCAuth.ssoSignIn(code);
}

const actions = {
  // CHECK: There is no usage of this action in the codebase
  async fetchUserDetails(context: UserContext, userEmail: string) {
    const reqParams = {
      email: userEmail
    };
    try {
      context.dispatch('fetchAccessToken').then(async () => {
        try {
          const response = await listUser({ queryParams: reqParams });
          return response.results?.[0] ?? null;
        } catch (e) {
          loggerService.error('Error in fetchUserDetails getUser', e);
        }
      });
    } catch (e) {
      loggerService.error('error in fetchUserDetails', e);
      return null;
    }
  },
  async fetchAccessToken(context: UserContext, payload?: { isUnauthorized?: boolean; error?: string }) {
    const { isUnauthorized = false, error = 'You are not authorized to access this resource' } = payload ?? {};
    const useGCAuth = context.rootState.event.useGCAuth;

    if (useGCAuth) {
      return fetchGCAuthAccessToken(context, isUnauthorized, error);
    }

    return fetchAmplifyAccessToken(context);
  },
  async loginUsingSSO(context: UserContext, payload: { code: string; eventId: string }) {
    loggerService.log('SSOLogin: Running sso login');
    const { code, eventId } = payload;
    const useGCAuth = context.rootState.event.useGCAuth;

    try {
      const ssoResponse = useGCAuth
        ? await getGCAuthTokenUsingSSO(code)
        : await ssoUser({ queryParams: { code, app: 'event' } });

      const result = useGCAuth
        ? await handleGCAuthSSOResponse(context, ssoResponse)
        : await handleAmplifySSOResponse(context, ssoResponse);

      const eventUser = await completeLoginFlow(context, result, eventId);

      loggerService.log('SSOLogin: Remove loader');
      (context.commit as any)('event/setSSOProcessing', null, { root: true });

      return eventUser;
    } catch (err) {
      handleSSOError(context, err);
    }
  },

  async forceNoCacheFetchUser(context: UserContext) {
    try {
      const data = await context.dispatch('fetchAccessToken');
      if (data) {
        let userDataResponse: any = null;
        try {
          //@ts-ignore
          userDataResponse = await infoUser({});
        } catch (e) {
          loggerService.warn('CacheAPI: error in forceNoCacheFetchUser, retrying', e);
          userDataResponse = await infoUser({});
        }
        const userData = makeUser({
          user: userDataResponse,
          event: '',
          id: '',
          blocked: false,
          view_as_attendee: false
        });

        context.commit('setCurrentUser', userData);
        try {
          if (userData.id) {
            analytics.identify(userData.id, {
              name: userData.name,
              email: userData.email
            });
          } else {
            loggerService.warn('Could not identify the user for segment!');
          }
        } catch (e) {
          loggerService.warn('error in forceNoCacheFetchUser', e);
        }
        return userData;
      }
      throw 'No User Logged In';
    } catch (error) {
      loggerService.warn('error in forceNoCacheFetchUser', error);
    }
  },
  fetchCurrentUser(context: UserContext, params: { force: boolean }) {
    const { force } = params || {};
    if (context.state.currentUser?.email && !force) {
      return Promise.resolve(context.state.currentUser);
    }
    return context.dispatch('forceNoCacheFetchUser');
  },
  setAttendeeFlagInLocalStorage(context: UserContext) {
    if (context.state.currentEventUser?.eventUser.view_as_attendee) {
      localStorage.setItem('was_viewing_as_attendee', 'true');
    }
  },
  removeAttendeeFlagInLocalStorage(context: UserContext) {
    localStorage.removeItem('was_viewing_as_attendee');
  },
  async loadEventUser(context: UserContext, { eventID, force }: { eventID: string; force: boolean }) {
    const u = context.state.currentEventUser;
    if (u && !force) {
      context.dispatch('setAttendeeFlagInLocalStorage');
      u.status = {
        authName: context.rootState.session.authName,
        ...context.rootState.session.rawData
      };
      return Promise.resolve(u);
    }
    try {
      const [eventUserResult, statusResult] = await Promise.allSettled([
        getEventUsersById(eventID, [context.rootGetters.getCurrentUserId]),
        context.dispatch(
          'session/getEmailUserStatus',
          {
            email: context.rootState.user.currentUser?.email,
            event: eventID,
            status_only: true,
            force_reload: force
          },
          { root: true }
        )
      ]);

      // Cannot verify registration or block status — must not allow access
      if (statusResult.status === 'rejected') {
        loggerService.error('error in fetching user status', statusResult.reason);
        return null;
      }

      const status = statusResult.value;

      let user: any;
      if (eventUserResult.status === 'fulfilled') {
        const {
          data: [userData]
        } = eventUserResult.value as any;
        user = {
          ...context.rootState.user.currentUser,
          ...makeUser(userData),
          status
        };
      } else {
        // Event-members API failed but security status is available — allow access with limited profile data
        loggerService.warn('Failed to load event user profile, proceeding with limited data', eventUserResult.reason);
        user = {
          ...context.rootState.user.currentUser,
          status
        };
      }

      context.dispatch('loadUserPermissions', { eventID, userId: user.id, role: user.event_role });
      context.commit('setCurrentEventUser', user);

      // check if user is speaker or organiser set cookie
      // so they dont become part of experimental build
      if (user.event_role != 0) {
        const isPreProd = getCookieValue('preProd');
        setCookieValue('admin', true);
        if (isPreProd) {
          deleteCookie('preProd');
          window.location.reload();
        }
      }

      context.dispatch('setAttendeeFlagInLocalStorage');
      context.commit(
        'event/setTracks' as any,
        mapTracksToTickets(context.rootState.event.event.tracks, context.rootState.event.event.ticketTypes, user),
        { root: true }
      );

      const agendaItems = formatAgenda(
        context.rootState.event.event.agenda,
        context.rootState.event.event.tracks,
        context.rootState.event.event.users
      );
      agendaItems.items.forEach(item => setIsLiveAndTrigger(context as any, item));
      agendaItems.broadcasts.forEach(item => setIsLiveAndTrigger(context as any, item));
      (context.commit as any)('event/setEventAgenda', agendaItems, {
        root: true
      });
      return user;
    } catch (error) {
      loggerService.error('error in fetching event user', error);
      return null;
    }
  },
  async updateUserProfileAndImage(context: UserContext, { image, user, isAsync = true }: any) {
    if (image && typeof image !== 'string') {
      const imageURL = await context.dispatch('putImage', {
        image,
        id: user.id
      });

      user.profile_picture_url = imageURL;
    } else if (!image && user.profile_picture_url) {
      await deleteUserSImage({});
      user.profile_picture_url = null;
    }

    const reqBody: any = {};

    const keysToUpdate = [
      'id',
      'first_name',
      'last_name',
      'title',
      'company',
      'location',
      'linkedin_url',
      'twitter_url',
      'tags',
      'profile_picture_url'
    ];

    const currentUserToUpdate: any = isAsync ? context.state.currentEventUser : context.state.currentUser;

    if (isAsync) {
      keysToUpdate.push('event');
      user['event'] = context.rootState.event.event.id;
      reqBody.event = context.rootState.event.event.id;
    }

    keysToUpdate.forEach(key => {
      if (user?.[key] !== currentUserToUpdate?.[key]) {
        reqBody[key] = user[key];
      }
    });

    const propertiesToUpdate = Object.keys(reqBody);
    if (propertiesToUpdate.length === 0) {
      return Promise.resolve();
    }

    await updateUserProfile({ userId: user.id, params: reqBody, isAsync });

    context.commit('optimisticallyUpdateCurrentUser', {
      user: reqBody,
      rootState: context.rootState
    });

    const currentUser = context.getters.getCurrentUser;
    if (currentUser) {
      EventBus.dispatch(ChatEvents.UpdateCurrentUser, {
        user: {
          id: currentUser.mmid || currentUser.id,
          name: currentUser.name || `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim(),
          avatar: currentUser.mediumimage || currentUser.thumbnail || currentUser.image,
          event_role: context.state.currentEventUser?.event_role,
          eventUserId: context.state.currentEventUser?.eventUser?.id
        }
      });
    }

    analytics.track('user_profile_updated');

    // Can't fetch the user just after updating because the
    // update api is now becoming async. This means, that
    // the server will return the wrong data if you refetch
    // it immediately. Your mind might be spinning by the implications
    // of this. Read the comment in the save function of Onboarding.vue
    // context.dispatch("forceNoCacheFetchUser");
  },
  async uploadRecordingThumbnail(context: UserContext, { image, id }: { image: File; id: string }) {
    const userAuthToken = context.state.accessToken;
    const smallImageFileName = `${id}-320x180.png`;
    // Store 1920x1080 for CL thumbnail
    const largeImageFileName = `${id}-1280x720.png`;

    await uploadProfileJpeg(`screenshots/reg/${smallImageFileName}`, image, userAuthToken, smallImageFileName);
    await uploadProfileJpeg(`screenshots/reg/${largeImageFileName}`, image, userAuthToken, largeImageFileName);
  },
  async putImage(context: UserContext, { image, id }: { image: File; id: string }) {
    const userAuthToken = context.state.accessToken;
    const imageUUID = uuidGen();
    const imageFileExtension = image.name.split('.').pop();
    const imageFileName = `${imageUUID}.${imageFileExtension}`;

    const key = `${getCloudFrontUrl('USER_PROFILE_IMAGE_KEY_PREFIX')}/${id}/${imageUUID}.${imageFileExtension}`;
    await uploadProfileJpeg(key, image, userAuthToken, imageFileName);
    return key;
  },
  async uploadSlides(context: UserContext, { presentation, id }: { presentation: File; id: string }) {
    const presentationExtension = presentation.name.split('.').pop();
    const fileName = `${id}_${UPLOAD_PREFIX}.${presentationExtension}`;

    const key = `organization/${context.rootState.event.event.organization}/slides/${fileName}`;
    const userAuthToken = context.state.accessToken;

    await uploadProfileJpeg(key, presentation, userAuthToken, fileName);
    return key;
  },
  signUserOut(context: UserContext) {
    return safeSignout()
      .then(() => {
        context.dispatch('RESET_USER');
      })
      .catch(err => {
        loggerService.error('error in signUserOut', err);
        context.dispatch('RESET_USER');
      });
  },
  setPrivateDiscussionRoom(context: UserContext, privateDiscussionRoom: { id: string }) {
    context.commit('setPrivateDiscussionRoom', privateDiscussionRoom);
  },
  RESET_USER(context: UserContext) {
    analytics.reset();
    context.commit('cleanState');
  },
  requestLiteModeSwitch(context: UserContext) {
    if (context.state.liteMode.supressedLiteModeConfirmation) {
      context.dispatch('toggleToLiteMode');
    } else {
      context.state.liteMode.liteModeState = 'CONFRIMLITEMODESWITCH';
    }
  },
  toggleToLiteMode(context: UserContext, supress_dialog?: boolean) {
    return new Promise((resolve, reject) => {
      try {
        const switchToLiteMode = () => {
          if (isLiteModeNotLoading(context)) {
            if (context.state.liteMode.liteModeAvailablityPromptTimeout) {
              clearTimeout(context.state.liteMode.liteModeAvailablityPromptTimeout);
            }
            closeJoiningDelayNetworkErrorModal();
            context.state.liteMode.liteModeState = 'LOADINGTOLITEMODE';
            setTimeout(() => {
              context.state.liteMode.liteModeState = 'LITEMODESWITCHSUCCESS';
              context.state.liteMode.liteModeActive = true;
              context.state.liteMode.supressedLiteModeConfirmation =
                supress_dialog || context.state.liteMode.supressedLiteModeConfirmation;

              updateLiteModeLocalStoreStates(context);
              context.commit('delayedLiteModeStateSwitchToLoaded');

              analytics.track('lite_mode', {
                enabled: true,
                event_id: context.rootState.route?.params?.eventID,
                userId: context.rootGetters['getCurrentUserId'],
                isAttendee: !context.rootGetters['event/isOrganizerOrSpeaker']
              });

              context.commit<any>('event/setThemeStyles', context.rootState);
              context.dispatch('liveVideoRoom/cleanupLiveVideoStore', null, {
                root: true
              });
              resolve(true);
            }, 3000);
          }
        };

        const i18n = context.rootState.event.translator?.i18n;
        const currentBroadcast = (context.rootState.event.event.agenda?.broadcasts ?? []).find(
          (b: any) => b.id === context.rootState.route.params.id
        );

        const isVideoQNARequestOpened = !!getVideoQNARequestForCurrentUserByStatus(currentBroadcast, 'OPEN');

        if (isVideoQNARequestOpened) {
          DialogService.show(
            GenericMessageDialog,
            {
              title: i18n.global.t('@@translate@@ Switch To Lite Mode'),
              message: i18n.global.t(
                '@@translate@@ Your pending Video Q&A request will be cancelled once you switch to lite mode'
              ),
              secondaryCTAText: i18n.global.t('@@translate@@ Stay on Live Mode'),
              resolveLabel: i18n.global.t('@@translate@@ Yes, I understand'),
              noCancel: true
            },
            {
              cancelOnClickOutside: false,
              on: {
                onHide: async () => {
                  context.dispatch('event/avroom/cancelVideoQNARequest', currentBroadcast, {
                    root: true
                  });
                  switchToLiteMode();
                },
                onSecondaryCTA: async () => reject()
              }
            }
          );
        } else {
          switchToLiteMode();
        }
      } catch (error) {
        const errorMessage = 'Turn on lite mode error!';

        analytics.track('lite_mode', {
          enabled: false,
          error: JSON.stringify(error),
          errorMessage,
          event_id: context.rootState.route?.params?.eventID,
          userId: context.rootGetters['getCurrentUserId'],
          isAttendee: !context.rootGetters['event/isOrganizerOrSpeaker']
        });

        loggerService.error(errorMessage, error);

        reject();
      }
    });
  },
  toggleToLiveMode(
    context: UserContext,
    { supress_dialog, litemode_was_not_avalilable }: { supress_dialog?: boolean; litemode_was_not_avalilable: boolean }
  ) {
    return new Promise((resolve, reject) => {
      try {
        if (isLiteModeNotLoading(context)) {
          context.state.liteMode.liteModeState = 'LOADINGTOLIVEMODE';
          setTimeout(() => {
            context.state.liteMode.liteModeState = 'LIVEMODESWITCHSUCCESS';

            context.state.liteMode.liteModeActive = false;
            context.state.liteMode.supressedLiteModeConfirmation =
              supress_dialog || context.state.liteMode.supressedLiteModeConfirmation;

            updateLiteModeLocalStoreStates(context);
            if (litemode_was_not_avalilable) {
              context.state.liteMode.willShowLiveModeAvailablePrompt = true;
              context.state.liteMode.liteModeState = 'INFORMWHENAVAILABLEPROMPT';
              setTimeout(() => {
                context.commit('delayedLiteModeStateSwitchToLoaded');
              }, 3000);
            } else {
              context.commit('delayedLiteModeStateSwitchToLoaded');
            }

            analytics.track('lite_mode', {
              enabled: false,
              event_id: context.rootState.route?.params?.eventID,
              userId: context.rootGetters['getCurrentUserId'],
              isAttendee: !context.rootGetters['event/isOrganizerOrSpeaker']
            });
            context.dispatch('liveVideoRoom/handleAgendaVideoPlaybackState', null, {
              root: true
            });
            resolve(true);
          }, 3000);
        }
      } catch (error) {
        const errorMessage = 'Turn off lite mode error!';

        analytics.track('lite_mode', {
          enabled: true,
          error: JSON.stringify(error),
          errorMessage,
          event_id: context.rootState.route?.params?.eventID,
          userId: context.rootGetters['getCurrentUserId'],
          isAttendee: !context.rootGetters['event/isOrganizerOrSpeaker']
        });

        loggerService.error(errorMessage, error);

        reject();
      }
    });
  },
  promptLiteModeSwitch(context: UserContext) {
    const currentTimeStamp = Date.now();
    if (
      checkFirstPromptOrBufferTimePassed(context.state.liteMode.lastLiteModeSwitchPromptTimestamp, currentTimeStamp)
    ) {
      context.dispatch('checkAndPromptLiteModeSwitch');
      context.state.liteMode.lastLiteModeSwitchPromptTimestamp = currentTimeStamp;
    }
  },
  promptLiveModeSwitch(context: UserContext) {
    const currentTimeStamp = Date.now();
    if (
      checkFirstPromptOrBufferTimePassed(context.state.liteMode.lastLiveModeSwitchPromptTimestamp, currentTimeStamp)
    ) {
      context.commit('checkAndPromptLiveModeSwitch');
      context.state.liteMode.lastLiveModeSwitchPromptTimestamp = currentTimeStamp;
    }
  },
  checkAndPromptLiteModeSwitch(context: UserContext) {
    const isCurrentRouteBroadcast = context.rootState.route?.fullPath?.includes('/broadcast');
    const broadcasts = context.rootState.event?.event?.agenda?.broadcasts;
    if (!isCurrentRouteBroadcast || !broadcasts || !context.rootState.route?.params?.id) {
      return;
    }
    const broadcast = broadcasts.find((broadcast: Broadcast) => broadcast.id === context.rootState.route?.params?.id);

    if (!broadcast.is_recording) {
      return;
    }

    if (
      !context.state.liteMode.liteModeActive &&
      !context.state.liteMode.supressedLiteModeConfirmation &&
      context.state.liteMode.liteModeState !== 'PROMPTLITEMODESWITCH'
    ) {
      context.state.liteMode.liteModeState = 'PROMPTLITEMODESWITCH';
      analytics.track('lite_mode_on_prompt');
    } else if (context.state.liteMode.liteModeState === 'PROMPTLIVEMODESWITCH') {
      context.state.liteMode.liteModeState = 'LOADED';
    }
  },
  toggleAgendaItemFavorite: debounce((context: UserContext, agendaItem: AgendaItem | Broadcast) => {
    const existingAgendaFavorite = context.getters['agendaFavorites'].find(agendaFavorite => {
      return agendaFavorite.agenda_item === agendaItem.id || agendaFavorite.broadcast === agendaItem.id;
    });

    if (existingAgendaFavorite) {
      context.commit('removeAgendaFavorite', existingAgendaFavorite);

      removeAgendaFavorite(existingAgendaFavorite).catch(error => {
        loggerService.warn('Unable to remove favorite', error);
        context.commit('addAgendaFavorite', existingAgendaFavorite);
      });
    } else {
      analytics.track('marking_agenda_item_as_favorite', {
        eventId: context.rootState.route?.params?.eventID,
        broadcastId: context.rootState.route?.params?.id,
        isAttendee: !context.rootGetters['event/isOrganizerOrSpeaker']
      });
      const isBroadcast = !!(agendaItem as Broadcast).broadcast_type;

      context.commit('addAgendaFavorite', {
        agenda_item: isBroadcast ? null : agendaItem.id,
        broadcast: isBroadcast ? agendaItem.id : null
      });

      const newFavorite = {
        event_user: context.state.currentEventUser?.eventUser?.id,
        agenda_item: isBroadcast ? null : agendaItem.id,
        broadcast: isBroadcast ? agendaItem.id : null
      };

      addAgendaFavorite(newFavorite)
        .then(res => {
          context.commit('updateAgendaFavorite', res.data);
        })
        .catch(error => {
          if (error?.data?.event_user?.[0]?.code === 'required' && context.rootGetters['isEventOrganizer']) {
            NotificationService.toast(
              '@@translate@@ Unable to mark as favorite',
              '@@translate@@ Event organizers must enter the event to select favorites.',
              'error',
              null,
              10000
            );
          }

          loggerService.warn('Unable to add favorite', error);
          context.commit('removeAgendaFavorite', newFavorite);
        });
    }
  }, 200),
  showEventUserBadge(context: UserContext) {
    const eventUserId = context.state.currentEventUser?.eventUser.id;
    const themeStyles: any = context.rootState.event.themeStyles;
    const [qrForeground, qrBackground] = [
      themeStyles['--secondary-text-color'],
      themeStyles['--theme-secondary-color']
    ];
    const en = encodeURIComponent;
    const inPersonManagerUrl = getEnv('VUE_APP_INPERSONMANAGER');
    DialogService.show(IframeEmbedder, {
      src: `${inPersonManagerUrl}/qr/${eventUserId}?fgColor=${en(qrForeground)}&bgColor=${en(qrBackground)}&size=256`,
      height: 256,
      width: 256,
      border: 0
    });
  },
  async loadUserPermissions(
    context: UserContext,
    { eventID, userId, role }: { eventID: string; userId: string; role: number }
  ) {
    if ([0, 7].includes(role)) {
      loggerService.log('loadUserPermissions: user is either attendee or recorder bot', {
        eventID,
        userId,
        eventRole: role
      });

      return;
    }
    if (!eventID || !userId) {
      loggerService.warn('loadUserPermissions: eventID or userId is missing', { eventID, userId });
    }
    const permissions = await getAllPermissionsAttachedToUser({ queryParams: { event: eventID, user: userId } });
    context.commit('setPermissions', permissions || {});
  }
};

function checkFirstPromptOrBufferTimePassed(lastSwitchTimestamp: number | undefined, currentTimeStamp: number) {
  // 10 minutes represented in miliseconds
  const minTimebetweenEachPrompts = 60 * 10 * 1000;
  return (
    !lastSwitchTimestamp || (lastSwitchTimestamp && lastSwitchTimestamp + minTimebetweenEachPrompts < currentTimeStamp)
  );
}

function isLiteModeNotLoading(context: UserContext) {
  return (
    context.state.liteMode.liteModeState !== 'LOADINGTOLITEMODE' &&
    context.state.liteMode.liteModeState !== 'LOADINGTOLIVEMODE'
  );
}

function updateLiteModeLocalStoreStates(context: UserContext) {
  saveValueToLocalstore(LiteModeKeys.liteModeLocalStoreKey, context.state.liteMode.liteModeActive);
  saveValueToLocalstore(LiteModeKeys.supressedLiteModeStoreKey, context.state.liteMode.supressedLiteModeConfirmation);
}

export default actions;
